import React, { useContext, useState } from 'react';
import { UnitsService } from 'core/api/services/units';

const UnitsContext = React.createContext(null);

export const UnitsProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [units, setUnits] = useState([]);
    const [currentUnit, setCurrentUnit] = useState(null);

    const createUnit = async (unitData) => {
        try {
            setLoading(true);
            const result = await UnitsService.create(unitData);

            if (result) {
                await getUnits();
            }

            return result;
        } catch (err) {
            console.error('Error creating unit:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const getUnits = async ({ ignoreCache = false } = {}) => {
        try {
            setLoading(true);

            if (!ignoreCache && Array.isArray(units) && units?.length > 0) {
                return units;
            }

            const result = await UnitsService.list();
            setUnits(result || []);
            return result || [];
        } catch (err) {
            console.error('Error fetching units:', err);
            setUnits([]);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const getUnit = async (unitId) => {
        try {
            setLoading(true);
            const result = await UnitsService.get(unitId);
            setCurrentUnit(result);
            return result;
        } catch (err) {
            console.error('Error fetching unit:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const updateUnit = async (unitId, unitData) => {
        try {
            setLoading(true);
            const result = await UnitsService.update(unitId, unitData);

            if (result) {
                // Refresh the units list
                await getUnits({ ignoreCache: true });
                // Update current unit if it's the same one
                if (currentUnit && currentUnit.id === unitId) {
                    setCurrentUnit(result);
                }
            }

            return result;
        } catch (err) {
            console.error('Error updating unit:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const deleteUnit = async (unitId) => {
        try {
            setLoading(true);
            const result = await UnitsService.delete(unitId);

            if (result) {
                // Refresh the units list
                await getUnits();
                // Clear current unit if it's the same one
                if (currentUnit && currentUnit.id === unitId) {
                    setCurrentUnit(null);
                }
            }

            return result;
        } catch (err) {
            console.error('Error deleting unit:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const clearCurrentUnit = () => {
        setCurrentUnit(null);
    };

    return (
        <UnitsContext.Provider
            value={{
                loading,
                units,
                currentUnit,
                createUnit,
                getUnits,
                getUnit,
                updateUnit,
                deleteUnit,
                clearCurrentUnit,
                setCurrentUnit,
            }}
        >
            {children}
        </UnitsContext.Provider>
    );
};

UnitsProvider.displayName = 'UnitsProvider';

export const useUnits = () => useContext(UnitsContext);
